/**
 * Minimal focus trap for bottom sheets and modal overlays.
 *
 * Returns a release function that restores focus to the previously focused
 * element. Trap loops Tab/Shift+Tab across focusable descendants within
 * `container`. Container must already be in the DOM when called.
 */
export function trapFocus(container: HTMLElement): () => void {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const focusables = (): HTMLElement[] => {
    const sel = [
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return [...container.querySelectorAll<HTMLElement>(sel)]
      // Only exclude when aria-hidden is the truthy literal "true". An explicit
      // aria-hidden="false" means the element is back in the a11y tree.
      .filter((el) => el.getAttribute('aria-hidden') !== 'true');
  };

  // Focus the first focusable element so screen readers land inside the trap.
  const initial = focusables()[0];
  if (initial) {
    initial.focus();
  } else {
    container.setAttribute('tabindex', '-1');
    container.focus();
  }

  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Tab') return;
    const list = focusables();
    if (list.length === 0) { e.preventDefault(); return; }
    const first = list[0];
    const last = list[list.length - 1];
    if (!first || !last) { e.preventDefault(); return; }
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !container.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !container.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  document.addEventListener('keydown', onKey, true);

  return () => {
    document.removeEventListener('keydown', onKey, true);
    if (previouslyFocused && document.body.contains(previouslyFocused)) {
      previouslyFocused.focus();
    }
  };
}
